import { Loader } from "@circle-react-uikit/Loader";
import { Rail } from "@circle-react-uikit/Rail";
import { RailBarPortal } from "@circle-react/components/Layout/LegacyBridge";
import { useCurrentSpaceContext } from "@circle-react/contexts/currentSpaceContext";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useRailbar } from "@circle-react/hooks/chatsV2/useRailbar";
import { useElementHeight } from "@circle-react/hooks/utils/useElementHeight";
import { IconButton } from "@circleco/compass/components/IconButton";
import { SearchInput } from "@circleco/compass/components/SearchInput";
import { toLower } from "lodash";
import { useState } from "react";

import { t } from "@/i18n-js/instance";
import { useSmOrMdOrLgScreenMediaQuery } from "@/react/hooks/useMediaQuery";
import { useDebouncedValue } from "@/react/hooks/utils/useDebouncedValue";

import { EmptyState } from "./EmptyState";
import { MessageSearchResults } from "./MessageSearchResults";
import { useMessageSearch } from "./useMessageSearch";

export const ChatRoomMessageSearchCompass = () => {
  const { data: currentSpace } = useCurrentSpaceContext();
  const { isDmsPage, closeThreadsRailbar } = useRailbar();
  const isSmScreen = useSmOrMdOrLgScreenMediaQuery();
  const spaceName = currentSpace ? currentSpace.name : "DMs";
  const [searchValue, setSearchValue] = useState("");
  const { ref: headerRef, height: headerHeight } = useElementHeight();
  const debouncedValue = useDebouncedValue(searchValue, 300);
  const { messages, isLoading, fetchNextPage, hasNextPage } = useMessageSearch({
    debouncedValue,
    isDmsPage,
    currentSpace,
  });

  const isSearched = !!debouncedValue;

  const renderContent = () => {
    if (!isSearched) {
      return <EmptyState name={spaceName} />;
    }
    if (isLoading) {
      return (
        <div className="flex h-full items-center justify-center">
          <Loader />
        </div>
      );
    }
    return (
      <MessageSearchResults
        messages={messages}
        fetchNextPage={fetchNextPage}
        hasNextPage={Boolean(hasNextPage)}
      />
    );
  };

  return (
    <RailBarPortal>
      <div ref={headerRef}>
        <Rail.Header
          className={classNames(
            isSmScreen ? "!px-4 py-3 gap-x-2" : "!px-4 py-4 gap-x-3",
          )}
          hasNoBorder
        >
          {isSmScreen && (
            <IconButton
              icon="arrow-left"
              onClick={closeThreadsRailbar}
              aria-label={t("close")}
              variant="ghost"
              size="md"
            />
          )}
          <SearchInput
            placeholder={t("chat_space.search_messages.placeholder", {
              spaceName: toLower(spaceName),
            })}
            data-testid="v2-search"
            aria-autocomplete="list"
            value={searchValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearchValue(e.target.value)
            }
            onClear={() => setSearchValue("")}
          />
          <IconButton
            icon="cross"
            onClick={closeThreadsRailbar}
            aria-label={t("close")}
            size="md"
            variant="ghost"
          />
        </Rail.Header>
      </div>
      <Rail.Content
        noPadding
        heightGap={headerHeight}
        id="scrollableMessageSearch"
      >
        {renderContent()}
      </Rail.Content>
    </RailBarPortal>
  );
};
